import { getDefaultTicketPrintConfig } from "../services/ticketPrintConfigService";

/**
 * Con cuántos decimales se escribe cada cosa, y por qué son distintas.
 *
 * Hasta hoy esto vivía repartido: un `2` a mano en un componente, los del país
 * en otro, la precisión de cálculo colada en un tercero. Cada arreglo movía una
 * pieza y dejaba las demás donde estaban, y así el tiquete llegó a imprimir
 * «23.998,56288» mientras el cierre de caja pedía un importe que no se podía
 * teclear.
 *
 * Las reglas son cinco y no hay una sexta:
 *
 *   1. **Importe en pantalla** — los del país. Costa Rica escribe con cinco.
 *   2. **Importe en papel** — dos, o los que diga la configuración de impresión
 *      de ese país. Un ticket es estrecho y se lee de un vistazo.
 *   3. **Cantidad** — entera si el número es entero, y dos si no.
 *   4. **Porcentaje** — dos, siempre.
 *   5. **Inicio** — dos. Es un panel de un vistazo, no un documento.
 *
 * Lo que **no** está aquí es la precisión de *cálculo*: ésa la da
 * `resolveMoneyDecimals` en utils/money.js y no tiene nada que ver con cómo se
 * escribe un número. Confundirlas fue el origen de media jornada de defectos.
 */

/** Regla 4 y 5: no dependen del país. */
export const DECIMALES_DE_PORCENTAJE = 2;
export const DECIMALES_EN_INICIO = 2;

/**
 * Regla 1 · Un importe en un formulario o un listado.
 *
 * Costa Rica escribe con cinco decimales a propósito: es lo que pide el negocio,
 * y por eso los campos tienen que **saber leer** lo que muestran —de eso se
 * ocupa `campoDeMoneda`—. Bajarlos a dos fue una corrección equivocada: el
 * problema nunca fueron los cinco decimales, sino un campo que enseñaba
 * «515.628,40740» y no aceptaba que se lo tecleara de vuelta.
 */
export const decimalesEnPantalla = (country) => country?.decimals ?? 2;

/**
 * Regla 2 · Un importe impreso: ticket, tiquete o reporte en papel.
 *
 * No se toma del país sino de la configuración de impresión, que es donde cada
 * país declara lo suyo. Hoy son dos en todos menos Paraguay, que no usa
 * decimales.
 */
export const decimalesEnPapel = (codigoPais) =>
  getDefaultTicketPrintConfig(codigoPais)?.decimales ?? 2;

/**
 * Regla 3 · Una cantidad.
 *
 * Entera cuando lo que llega es entero —vender tres libros son «3», no
 * «3,00»— y con dos decimales cuando la cantidad es fraccionaria, que es lo que
 * pasa con lo que se vende por peso o por medida.
 *
 * Ojo: esto es cómo se **escribe**. Redondear una cantidad es otra cosa y la
 * hace `roundQuantity`, que trabaja a tres decimales.
 */
export const decimalesDeCantidad = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return 0;
  return Number.isInteger(n) ? 0 : 2;
};
